// ===================================================================
// File: client/src/scenes/loans/LoanApplicationForm.jsx
// Purpose: Dialog form for creating a loan application + show equity
// ===================================================================
import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  MenuItem,
  IconButton,
  Typography,
  Paper,
  Divider,
  Alert,
  Snackbar,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { gql, useQuery } from "@apollo/client";
import { GET_LOAN_PRODUCTS } from "../../graphql/loans";
import { userClient } from "state/apolloClient";

// ============================================================
// Member Equity (by logged-in user)
// ============================================================
const GET_MEMBER_EQUITY_BY_USER = gql`
  query GetMemberEquityByUser {
    memberEquityByUser {
      availableEquity
      groupId
      totalGuaranteeLocked
      totalPaidContributions
      userId
      username
    }
  }
`;

// ============================================================
// Eligible Guarantors (equity > 0, excluding logged-in user)
// ============================================================
const GET_ELIGIBLE_GUARANTORS_EQUITY = gql`
  query GetEligibleGuarantorsEquity {
    eligibleGuarantorsEquity {
      availableEquity
      groupId
      totalGuaranteeLocked
      totalPaidContributions
      userId
      username
    }
  }
`;

// Simple KE phone normalizer
const normalizeKenyanPhone = (raw) => {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (digits.startsWith("2547") && digits.length === 12) return "+" + digits;
  if (digits.startsWith("07") && digits.length === 10) return "+254" + digits.slice(1);
  if (raw.startsWith("+2547") && raw.length === 13) return raw;
  return raw;
};

const currency = (n) =>
  typeof n === "number" && !Number.isNaN(n)
    ? n.toLocaleString()
    : n || n === 0
    ? Number(n).toLocaleString()
    : "-";

// Robustly extract SpStatusResult from whatever onSubmit returns
const extractSpStatus = (result) => {
  // Case 1: parent returns the SpStatusResult directly
  if (result?.status) return result;

  // Case 2: parent returns Apollo mutate result: { data: { bookLoanApplication: {...} } }
  const sp = result?.data?.bookLoanApplication;
  if (sp?.status) return sp;

  // Case 3: parent returns Axios/fetch-like: { data: {...} }
  const sp2 = result?.data?.data?.bookLoanApplication;
  if (sp2?.status) return sp2;

  return null;
};

const LoanApplicationForm = ({ open, onClose, onSubmit, submitting }) => {
  const { data: productsData, loading: loadingProducts } = useQuery(GET_LOAN_PRODUCTS, {
    skip: !open,
  });

  // Logged-in user's equity
  const {
    data: equityData,
    loading: loadingEquity,
    error: equityError,
    networkStatus,
  } = useQuery(GET_MEMBER_EQUITY_BY_USER, {
    skip: !open,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  // Eligible guarantors (equity > 0, excluding logged-in user)
  const {
    data: guarantorsData,
    loading: loadingGuarantors,
    error: guarantorsError,
  } = useQuery(GET_ELIGIBLE_GUARANTORS_EQUITY, {
    skip: !open,
    fetchPolicy: "cache-and-network",
    // If this endpoint lives on a different client, uncomment:
    // client: userClient,
  });

  const products = productsData?.loanProducts || [];

  // ---- Use eligibleGuarantorsEquity for guarantors dropdown ----
  const rawGuarantors = guarantorsData?.eligibleGuarantorsEquity || [];
  const guarantors = rawGuarantors.map((m) => ({
    id: m.userId,
    username: m.username ?? `User #${m.userId}`,
    availableEquity: Number(m.availableEquity ?? 0),
  }));

  // --- Equity snapshot (protect against transient nulls) ---
  const [equitySnapshot, setEquitySnapshot] = useState(null);

  useEffect(() => {
    const e = equityData?.memberEquityByUser;
    if (e) setEquitySnapshot(e);
  }, [equityData]);

  useEffect(() => {
    if (!open) setEquitySnapshot(null);
  }, [open]);

  const equity = equitySnapshot || equityData?.memberEquityByUser || null;

  const myUserId = Number(equity?.userId ?? 0);
  const myUsername = equity?.username ?? "You";
  const availableEquity = Number(equity?.availableEquity ?? 0);
  const totalPaid = Number(equity?.totalPaidContributions ?? 0);
  const totalLocked = Number(equity?.totalGuaranteeLocked ?? 0);

  const [form, setForm] = useState({
    loanProductId: "",
    requestedAmount: "",
    disbursementPhoneNumber: "",
    guarantors: [{ guarantorId: "", amountPledged: "" }],
  });

  // In-app notifications (replaces browser alerts)
  const [toast, setToast] = useState({
    open: false,
    severity: "success", // "success" | "error" | "warning" | "info"
    message: "",
  });

  const showToast = (message, severity = "info") => {
    setToast({ open: true, severity, message });
  };

  const closeToast = (_, reason) => {
    if (reason === "clickaway") return;
    setToast((t) => ({ ...t, open: false }));
  };

  // Reset on close/open
  useEffect(() => {
    if (!open) {
      setForm({
        loanProductId: "",
        requestedAmount: "",
        disbursementPhoneNumber: "",
        guarantors: [{ guarantorId: "", amountPledged: "" }],
      });
    }
  }, [open]);

  const requested = useMemo(
    () => Number.parseFloat(form.requestedAmount) || 0,
    [form.requestedAmount]
  );

  /**
   * NEW COLLATERAL MODEL (matches backend rule: guarantors must sum == requested)
   *
   * - Self-guarantee is a guarantor record using the logged-in user's id.
   * - Self pledge amount = min(availableEquity, requested)
   * - Remaining must be covered by other guarantors.
   */
  const selfPledge = useMemo(() => {
    if (!requested || requested <= 0) return 0;
    return Math.max(0, Math.min(availableEquity, requested));
  }, [availableEquity, requested]);

  const remainingToCover = useMemo(() => {
    if (!requested || requested <= 0) return 0;
    return Math.max(0, requested - selfPledge);
  }, [requested, selfPledge]);

  const otherPledged = useMemo(
    () =>
      form.guarantors.reduce(
        (sum, g) => sum + (parseFloat(g.amountPledged) || 0),
        0
      ),
    [form.guarantors]
  );

  const totalCollateral = useMemo(() => selfPledge + otherPledged, [selfPledge, otherPledged]);

  const pledgeOk = useMemo(() => {
    if (!requested || requested <= 0) return false;
    // backend expects equal; we allow tiny tolerance for UI typing
    return Math.abs(totalCollateral - requested) <= 0.01;
  }, [totalCollateral, requested]);

  const handleGuarantorChange = (idx, key, value) => {
    setForm((prev) => ({
      ...prev,
      guarantors: prev.guarantors.map((g, i) => (i === idx ? { ...g, [key]: value } : g)),
    }));
  };

  const addGuarantorRow = () =>
    setForm((prev) => ({
      ...prev,
      guarantors: [...prev.guarantors, { guarantorId: "", amountPledged: "" }],
    }));

  const removeGuarantorRow = (idx) =>
    setForm((prev) => ({
      ...prev,
      guarantors: prev.guarantors.filter((_, i) => i !== idx),
    }));

  const disabled = submitting || loadingProducts || loadingGuarantors;

  const handleSubmit = async () => {
    if (!form.loanProductId) return showToast("Please choose a loan product.", "warning");
    if (!requested || requested <= 0) return showToast("Enter a valid requested amount.", "warning");
    if (!form.disbursementPhoneNumber)
      return showToast("Enter a disbursement phone number.", "warning");

    // If user has no equity and remaining is full amount, they MUST add guarantors
    if (requested > 0 && totalCollateral <= 0)
      return showToast("This loan requires collateral. Add guarantors (or have equity).", "warning");

    // If remaining > 0, other guarantors must cover remaining
    if (remainingToCover > 0 && otherPledged < remainingToCover)
      return showToast(
        `Guarantors must cover the remaining KES ${currency(remainingToCover)}.`,
        "warning"
      );

    // Also enforce total == requested (backend rule)
    if (!pledgeOk)
      return showToast(
        `Collateral must match the requested amount. Total collateral is KES ${currency(totalCollateral)} but requested is KES ${currency(requested)}.`,
        "warning"
      );

    // Build guarantors payload: include self-guarantee if selfPledge > 0
    const otherGuarantors = form.guarantors
      .filter((g) => g.guarantorId && g.amountPledged)
      .map((g) => ({
        guarantorId: Number(g.guarantorId),
        amountPledged: Number(g.amountPledged),
      }));

    const payloadGuarantors = [
      ...(selfPledge > 0 && myUserId > 0
        ? [{ guarantorId: myUserId, amountPledged: Number(selfPledge) }]
        : []),
      ...otherGuarantors,
    ];

    // Final safety: sum must equal requested (tolerance)
    const pledgedSum = payloadGuarantors.reduce((s, g) => s + (Number(g.amountPledged) || 0), 0);
    if (Math.abs(pledgedSum - requested) > 0.01) {
      return showToast(
        `Guarantorship must equal requested amount. Total pledged is KES ${currency(pledgedSum)}.`,
        "warning"
      );
    }

    const cleaned = {
      disbursementPhoneNumber: normalizeKenyanPhone(form.disbursementPhoneNumber),
      loanProductId: Number(form.loanProductId),
      requestedAmount: Number(requested),
      guarantors: payloadGuarantors,
    };

    try {
      const result = await onSubmit(cleaned);

      // ✅ IMPORTANT: Don't treat "network success" as "business success"
      const sp = extractSpStatus(result);

      if (!sp) {
        showToast("Unexpected response from server. Please try again.", "error");
        return;
      }

      if (String(sp.status).toLowerCase() !== "success") {
        showToast(sp.errorMessage || "Loan booking failed.", "error");
        return;
      }

      showToast("Loan application submitted successfully.", "success");

      // Optional: reset form after success (keeps dialog open)
      setForm({
        loanProductId: "",
        requestedAmount: "",
        disbursementPhoneNumber: "",
        guarantors: [{ guarantorId: "", amountPledged: "" }],
      });
    } catch (err) {
      showToast("Failed to submit loan application. Please try again.", "error");
    }
  };

  const showEquityLoading = loadingEquity || networkStatus === 4;

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Apply for a Loan</DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {/* ================= Equity Strip ================= */}
            <Box mb={2}>
              {equityError ? (
                <Alert severity="warning">Unable to load your equity right now.</Alert>
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8, mb: 1 }}>
                    Your Equity Position
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ opacity: 0.75 }}>
                        Total Paid Contributions
                      </Typography>
                      <Typography variant="h6">
                        {showEquityLoading && !equity ? "Loading..." : `KES ${currency(totalPaid)}`}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ opacity: 0.75 }}>
                        Locked as Guarantees
                      </Typography>
                      <Typography variant="h6">
                        {showEquityLoading && !equity
                          ? "Loading..."
                          : `KES ${currency(totalLocked)}`}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ opacity: 0.75 }}>
                        Available Equity
                      </Typography>
                      <Typography variant="h6">
                        {showEquityLoading && !equity
                          ? "Loading..."
                          : `KES ${currency(availableEquity)}`}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Box>

            {/* If eligible guarantors query fails */}
            {guarantorsError && (
              <Box mb={2}>
                <Alert severity="warning">Unable to load guarantors list right now.</Alert>
              </Box>
            )}

            {/* ================= Loan Inputs ================= */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Loan Product"
                  value={form.loanProductId}
                  onChange={(e) => setForm((f) => ({ ...f, loanProductId: e.target.value }))}
                  disabled={disabled}
                >
                  {products.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Requested Amount"
                  value={form.requestedAmount}
                  onChange={(e) => setForm((f) => ({ ...f, requestedAmount: e.target.value }))}
                  inputProps={{ min: 0 }}
                  disabled={disabled}
                  helperText={
                    requested > 0
                      ? remainingToCover === 0
                        ? `Self-guarantee: KES ${currency(selfPledge)} (you fully cover this loan)`
                        : `Self-guarantee: KES ${currency(selfPledge)}. Remaining to cover: KES ${currency(
                            remainingToCover
                          )}`
                      : " "
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Disbursement Phone Number"
                  placeholder="07XXXXXXXX or +2547XXXXXXXX"
                  value={form.disbursementPhoneNumber}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      disbursementPhoneNumber: e.target.value,
                    }))
                  }
                  disabled={disabled}
                />
              </Grid>
            </Grid>

            {/* ================= Collateral Summary ================= */}
            {requested > 0 && (
              <Box mt={2}>
                <Alert severity={pledgeOk ? "success" : "info"}>
                  <strong>Collateral:</strong> Self ({myUsername}) KES {currency(selfPledge)} + Others KES{" "}
                  {currency(otherPledged)} = <strong>KES {currency(totalCollateral)}</strong>. Requested:{" "}
                  <strong>KES {currency(requested)}</strong>.
                </Alert>
              </Box>
            )}

            {/* ================= Guarantors ================= */}
            {requested > 0 && remainingToCover > 0 && (
              <Box mt={3} component={Paper} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Other Guarantors (Cover Remaining)
                </Typography>

                <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                  Remaining to cover: <strong>KES {currency(remainingToCover)}</strong>
                </Typography>

                <Divider sx={{ mb: 2 }} />

                {form.guarantors.map((g, idx) => (
                  <Grid container spacing={2} key={idx} alignItems="center">
                    <Grid item xs={12} md={7}>
                      <TextField
                        select
                        fullWidth
                        label={`Guarantor #${idx + 1}`}
                        value={g.guarantorId}
                        onChange={(e) =>
                          handleGuarantorChange(idx, "guarantorId", e.target.value)
                        }
                        disabled={disabled}
                      >
                        {guarantors.map((m) => (
                          <MenuItem key={m.id} value={m.id}>
                            {m.username} — KES {currency(m.availableEquity)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={10} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Amount Pledged"
                        value={g.amountPledged}
                        onChange={(e) =>
                          handleGuarantorChange(idx, "amountPledged", e.target.value)
                        }
                        disabled={disabled}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>

                    <Grid item xs={2} md={1}>
                      <IconButton
                        onClick={() => removeGuarantorRow(idx)}
                        disabled={disabled || form.guarantors.length === 1}
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}

                <Button startIcon={<Add />} onClick={addGuarantorRow} disabled={disabled}>
                  Add Guarantor
                </Button>

                <Box mt={2}>
                  <Typography variant="body2">
                    Others pledged: <strong>KES {currency(otherPledged)}</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    color={otherPledged >= remainingToCover ? "success.main" : "warning.main"}
                  >
                    {otherPledged >= remainingToCover
                      ? "OK: Other guarantors cover the remaining amount"
                      : `Need at least KES ${currency(remainingToCover)} from other guarantors`}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={disabled}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={disabled}>
            {submitting ? "Submitting..." : "Submit Application"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* In-app toast notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LoanApplicationForm;